import {
  getActiveProposals,
  getEvaluatorMvfProfiles,
} from "@engine/round/accessors";
import type {
  Funder,
  Recommender,
  RoundAggregate,
} from "@engine/round/aggregate";
import {
  algoInputPartsEqual,
  getRecommenderAlgoInputParts,
} from "@engine/round/aggregate";
import type Matrix from "algorithm/matrix";

export type SyntheticRecommender = {
  syntheticId: string;
  recommenderId: string;
  funderIds: string[];
  excludedProposalIds: string[];
  budget: number;
};

export function computeSyntheticRecommendersForRecommender({
  recommenderId,
  funderAllocationsToRecommender,
  excludedByFunder,
  proposalsRecommenderFunds,
}: {
  recommenderId: string;
  funderAllocationsToRecommender: Record<string, number>;
  excludedByFunder: Record<string, Set<string>>;
  proposalsRecommenderFunds: Set<string>;
}): SyntheticRecommender[] {
  // Group funders by their effective exclusion set (excluded ∩ proposals-funded).
  const groups = new Map<
    string,
    { excludedProposalIds: string[]; funderIds: string[]; budget: number }
  >();

  for (const [funderId, amount] of Object.entries(
    funderAllocationsToRecommender,
  )) {
    // A funder contributing nothing to this recommender forms no synthetic —
    // their exclusion can't affect money that doesn't flow through here, and an
    // extra zero-budget synthetic would spuriously split the recommender (and
    // skip the identity phase-1 short-circuit). Mirrors the engine's
    // funder-accounts path, which only keeps positive allocations.
    if (amount <= 0) {
      continue;
    }

    const rawExcluded = excludedByFunder[funderId];
    const effective = rawExcluded
      ? [...rawExcluded]
          .filter((id) => proposalsRecommenderFunds.has(id))
          .sort()
      : [];
    const key = effective.join(",");
    const existing = groups.get(key);
    if (existing == null) {
      groups.set(key, {
        excludedProposalIds: effective,
        funderIds: [funderId],
        budget: amount,
      });
    } else {
      existing.funderIds.push(funderId);
      existing.budget += amount;
    }
  }

  return [...groups.values()].map((group) => ({
    syntheticId:
      group.excludedProposalIds.length === 0
        ? recommenderId
        : `${recommenderId}::${group.excludedProposalIds.join(",")}`,
    recommenderId,
    funderIds: group.funderIds,
    excludedProposalIds: group.excludedProposalIds,
    budget: group.budget,
  }));
}

const fundableProposalIdsCache: {
  cacheForParts: unknown[] | null;
  activeProposalIds: Set<string> | null;
  byRecommenderId: Map<string, Set<string>>;
} = {
  cacheForParts: null,
  activeProposalIds: null,
  byRecommenderId: new Map(),
};

function getFundableProposalIds(
  round: RoundAggregate,
  recommender: Recommender,
  minGrantExcludedProposalIds: { [proposalId: string]: number },
): Set<string> {
  const parts = getRecommenderAlgoInputParts(round);
  if (
    fundableProposalIdsCache.cacheForParts == null ||
    !algoInputPartsEqual(fundableProposalIdsCache.cacheForParts, parts)
  ) {
    fundableProposalIdsCache.cacheForParts = parts;
    fundableProposalIdsCache.activeProposalIds = null;
    fundableProposalIdsCache.byRecommenderId = new Map();
  }
  let fundable = fundableProposalIdsCache.byRecommenderId.get(recommender.id);
  if (fundable == null) {
    fundableProposalIdsCache.activeProposalIds ??= new Set(
      getActiveProposals(round).map((proposal) => proposal.id),
    );
    const activeProposalIds = fundableProposalIdsCache.activeProposalIds;
    fundable = new Set(
      Object.entries(getEvaluatorMvfProfiles(round, recommender.id).standard)
        .filter(
          ([proposalId, mvf]) =>
            activeProposalIds.has(proposalId) &&
            mvf.some((point) => point.value > 0),
        )
        .map(([proposalId]) => proposalId),
    );
    fundableProposalIdsCache.byRecommenderId.set(recommender.id, fundable);
  }
  if (Object.keys(minGrantExcludedProposalIds).length === 0) {
    return fundable;
  }
  return new Set(
    [...fundable].filter(
      (proposalId) => !(proposalId in minGrantExcludedProposalIds),
    ),
  );
}

// Build the synthetic recommenders for every active recommender from the
// funder-layer output. `funderAllocations[funderIndex][recommenderId]` is the
// amount that funder sent to that recommender.
export function buildSyntheticRecommendersFromFunderAccounts(
  round: RoundAggregate,
  funderAccounts: Matrix<"funder", "recommender+hold">,
  funders: Funder[],
  orderedRecommenders: Recommender[],
  recommenderIndicesMap: { [recommenderId: string]: number },
  minGrantExcludedProposalIds: { [proposalId: string]: number } = {},
): SyntheticRecommender[] {
  // Per-funder exclusion sets (only funders that excluded something appear).
  const excludedByFunder: Record<string, Set<string>> = {};
  for (const funder of funders) {
    const excludedProposalIds = funder.excludedProposalIds ?? [];
    if (excludedProposalIds.length > 0) {
      excludedByFunder[funder.id] = new Set(excludedProposalIds);
    }
  }

  const synthetics: SyntheticRecommender[] = [];
  for (const recommender of orderedRecommenders) {
    const recommenderCol = recommenderIndicesMap[recommender.id];
    if (recommenderCol == null) {
      continue;
    }

    // What this recommender's funder-layer budget was, split by funder.
    const funderAllocationsToRecommender: Record<string, number> = {};
    funders.forEach((funder, funderIndex) => {
      const amount = funderAccounts.get(funderIndex, recommenderCol);
      if (amount > 0) {
        funderAllocationsToRecommender[funder.id] = amount;
      }
    });

    // Proposals this recommender actually funds (nonzero standard MVF), so an
    // exclusion of a proposal the recommender doesn't fund is a no-op.
    const proposalsRecommenderFunds = getFundableProposalIds(
      round,
      recommender,
      minGrantExcludedProposalIds,
    );

    const recommenderSynthetics = computeSyntheticRecommendersForRecommender({
      recommenderId: recommender.id,
      funderAllocationsToRecommender,
      excludedByFunder,
      proposalsRecommenderFunds,
    });

    if (recommenderSynthetics.length === 0) {
      // The helper returns [] when the recommender received no funder dollars.
      // Preserve today's behavior: every active recommender stays included.
      synthetics.push({
        syntheticId: recommender.id,
        recommenderId: recommender.id,
        funderIds: [],
        excludedProposalIds: [],
        budget: 0,
      });
    } else {
      synthetics.push(...recommenderSynthetics);
    }
  }

  return synthetics;
}

// Build synthetics from a caller-supplied per-recommender budget split, rather
// than from a funder-layer accounts matrix. Used by the Incremental Evals matrix
// (getAllRecommenderAllocations), whose recommender budgets are combined across
// funders up front and whose per-funder split is derived from the round's net
// recommender allocations. `funderAllocationsByRecommender[recId][funderId]` must
// sum (over funders) to `recommenderBudgets[recId]`, so each recommender's
// synthetics pool back to its combined budget. A recommender missing from the
// split falls back to one unsplit synthetic recommender carrying the full
// budget, so every active recommender stays included.
export function buildSyntheticRecommendersFromFunderAllocationSplit(
  round: RoundAggregate,
  orderedRecommenders: Recommender[],
  funderAllocationsByRecommender: {
    [recommenderId: string]: { [funderId: string]: number };
  },
  excludedByFunder: { [funderId: string]: string[] },
  recommenderBudgets: { [recommenderId: string]: number },
): SyntheticRecommender[] {
  // Per-funder exclusion sets (only funders that excluded something appear).
  const excludedByFunderSets: Record<string, Set<string>> = {};
  for (const [funderId, excludedIds] of Object.entries(excludedByFunder)) {
    if (excludedIds.length > 0) {
      excludedByFunderSets[funderId] = new Set(excludedIds);
    }
  }

  const synthetics: SyntheticRecommender[] = [];
  for (const recommender of orderedRecommenders) {
    const funderAllocationsToRecommender =
      funderAllocationsByRecommender[recommender.id];

    // No split for this recommender: keep one synthetic recommender carrying
    // its full combined budget so it stays included.
    if (funderAllocationsToRecommender == null) {
      synthetics.push({
        syntheticId: recommender.id,
        recommenderId: recommender.id,
        funderIds: [],
        excludedProposalIds: [],
        budget: recommenderBudgets[recommender.id] ?? 0,
      });
      continue;
    }

    // Proposals this recommender actually funds (nonzero standard MVF), so an
    // exclusion of a proposal the recommender doesn't fund is a no-op.
    const proposalsRecommenderFunds = getFundableProposalIds(
      round,
      recommender,
      {},
    );

    const recommenderSynthetics = computeSyntheticRecommendersForRecommender({
      recommenderId: recommender.id,
      funderAllocationsToRecommender,
      excludedByFunder: excludedByFunderSets,
      proposalsRecommenderFunds,
    });

    if (recommenderSynthetics.length === 0) {
      // The helper returns [] when the recommender received no funder dollars.
      // Preserve today's behavior: every active recommender stays included.
      synthetics.push({
        syntheticId: recommender.id,
        recommenderId: recommender.id,
        funderIds: [],
        excludedProposalIds: [],
        budget: recommenderBudgets[recommender.id] ?? 0,
      });
    } else {
      synthetics.push(...recommenderSynthetics);
    }
  }

  return synthetics;
}
